import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ViewportTitleBar } from '@/components/AppViewport';
import { isDesktopRuntime } from './desktopDetection';

/**
 * Who is drawing the title bar, so that the screens without one are still draggable.
 *
 * ## The defect this exists for
 *
 * ⛔ On macOS the window has no system title bar of its own — `titleBarStyle: Overlay` puts the
 * traffic lights over the page and leaves the rest of that strip to us. The only thing that makes
 * any of it draggable is `data-tauri-drag-region`, and the only component that carried it was the
 * tab bar, which is rendered by `Layout` and therefore only ever appears once somebody is signed in
 * to a deployment.
 *
 * So on `/connect`, `/login`, `/register` and `/cli-auth` — every screen a person sees BEFORE they
 * have an account, including the very first one after installing — **the window could not be moved
 * at all**. Not a subtle bug once you are in it: the window is wherever it was restored to, and the
 * traffic lights are the only working controls on it.
 *
 * ## Why a registry rather than a list of paths
 *
 * ⚠️ A hard-coded "these routes have no tab bar" list is a second statement of the routing table,
 * and it goes stale the moment a screen is added outside `Layout` — silently, and in the direction
 * that reintroduces exactly this defect. Instead the tab bar announces itself, and the fallback
 * strip renders when nothing has. A new screen outside `Layout` is draggable by default.
 */
const DesktopTitleBarContext = createContext<{
  count: number;
  claim: () => () => void;
}>({ count: 0, claim: () => () => undefined });

export function DesktopTitleBarProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);
  const value = useMemo(
    () => ({
      count,
      claim: () => {
        setCount((current) => current + 1);
        return () => setCount((current) => Math.max(0, current - 1));
      },
    }),
    [count],
  );
  return (
    <DesktopTitleBarContext.Provider value={value}>{children}</DesktopTitleBarContext.Provider>
  );
}

/** Called by whatever draws a real title bar, for as long as it is mounted. */
export function useClaimDesktopTitleBar(active: boolean): void {
  const { claim } = useContext(DesktopTitleBarContext);
  useEffect(() => {
    if (!active) return undefined;
    return claim();
    // `claim` is recreated whenever the count changes; depending on it would release and re-take the
    // claim on every change and leave the fallback flickering between them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

/**
 * A bare draggable strip, for the screens that draw no title bar of their own.
 *
 * It is deliberately the same height as the real one, so moving between a signed-out screen and a
 * signed-in one does not shift the page under the reader.
 */
export function DesktopTitleBarFallback() {
  const { count } = useContext(DesktopTitleBarContext);
  if (!isDesktopRuntime() || count > 0) return null;
  /*
   * I-2378: in the title-bar slot, the window's first row. It stays fixed and takes no space, unless
   * a top bar shows: then it is a row of its own above the bar (`index.css`), so the bar is never
   * under the traffic lights or the drag region.
   */
  return (
    <ViewportTitleBar>
      <div
        className="desktop-titlebar desktop-titlebar--fallback"
        data-tauri-drag-region=""
        aria-hidden="true"
      >
        <div className="desktop-titlebar__drag-space" data-tauri-drag-region="" />
      </div>
    </ViewportTitleBar>
  );
}

/**
 * I-1423: the tab bar's place, held from the first frame of a signed-in screen until the tab bar can
 * draw, so the page under it is never moved down by its arrival. The tab bar draws nothing until its
 * window's stored tabs are read (and its chunk is a chunk of its own), and it is in flow above the
 * page: arriving late, it pushed every row down by its height.
 *
 * In flow and the bar's own height (`.desktop-titlebar`), with the window's drag region, so the window
 * moves as it will once the tabs are drawn. It claims nothing: `DesktopTitleBarFallback` stays on
 * over it until the tab bar claims the bar.
 */
export function DesktopTitleBarPlaceholder() {
  return (
    <div
      className="desktop-titlebar"
      data-desktop-titlebar-pending=""
      data-tauri-drag-region=""
      aria-hidden="true"
    />
  );
}
